import { ExportType } from 'api/export';
import { DownloadOption } from 'constants/download';
import { icons8Download, icons8IllustrationDownload } from 'libs/e1api/icons8';
import { RootState, store } from 'modules/Editor/store';
import { selectPage, selectWidgets } from 'modules/Editor/store/infographSelector';
import { PageId } from 'types/idTypes';
import { WidgetType } from 'types/widget.types';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';
import { getPageWidgetIds, getWidgetTypeFromId } from 'widgets/Widget.helpers';

/**
 * get export type object
 *
 * @param {DownloadOption} downloadOption
 * @returns {type: ExportType} export type object
 */
export const getExportType = (downloadOption: DownloadOption): { type: ExportType } => {
  let exportType;
  switch (downloadOption) {
    case DownloadOption.InteractivePDF:
      exportType = ExportType.pdf;
      break;
    case DownloadOption.PowerPoint:
      exportType = ExportType.pptx;
      break;
    default:
      exportType = ExportType.png;
      break;
  }
  return { type: exportType };
};

/**
 * Retrieve all unique icons8 icons from the given page ids
 *
 * @param pageIds
 * @returns { iconId: string, isIllustration: boolean}[] Array of unique icons8 icons/illustrations
 * - isIllustration flag used to determine whether to user normal icons8 download endpoint vs illustration download endpoint
 */
export const getIcons8Icons = (pageIds: PageId[]): { iconId: string; isIllustration: boolean }[] => {
  const storeState = store.getState() as RootState;
  const widgetsMap = selectWidgets(storeState);

  // Map to keep track of unique icons
  const iconIds: { [key: string]: boolean } = {};

  const icons: { iconId: string; isIllustration: boolean }[] = [];
  for (const pageId of pageIds) {
    const page = selectPage(pageId)(storeState);
    const widgetIds = getPageWidgetIds(page, widgetsMap);

    widgetIds.forEach((widgetId) => {
      const widgetType = getWidgetTypeFromId(widgetId);
      if (widgetType !== WidgetType.Icon) return;

      const widget = widgetsMap[widgetId] as IconWidgetData;

      // Skip if already added this icon
      if (iconIds[widget.iconId]) return;

      const isIcons8 = !!widget.iconMetadata?.iconPath;
      const isIllustration = !!widget.iconMetadata?.isIllustration;
      if (isIcons8) {
        iconIds[widget.iconId] = true;
        icons.push({ iconId: widget.iconId, isIllustration: false });
      } else if (isIllustration) {
        iconIds[widget.iconId] = true;
        icons.push({ iconId: widget.iconId, isIllustration: true });
      }
    });
  }

  return icons;
};

/**
 * Trigger icons8 download request to count towards our quota
 * Should be triggered on a successful download
 *
 * @param pageIds Page ids included in download
 */
export const countIcons8Quota = async (pageIds: PageId[]) => {
  const icons = getIcons8Icons(pageIds);

  // Set the maximum number of simultaneous requests and the maximum number of icons to download
  const maxRequests = 4;
  const maxIconsPerDownload = 50;
  let iconsCount = 0;

  // Function to download a single icon and count it towards the icons8 quota
  async function downloadIcon({ iconId, isIllustration }: { iconId: string; isIllustration: boolean }) {
    if (iconsCount < maxIconsPerDownload) {
      try {
        // Make request to the icons8_download endpoint
        if (!isIllustration) {
          await icons8Download(iconId, true);
        } else {
          await icons8IllustrationDownload(iconId, true);
        }
      } catch (error) {
        console.error('Error downloading icon:', error);
      }

      // Increment the iconsCount and process the next icon
      iconsCount++;
      processNextIcon();
    }
  }

  // Function to start processing the next icon in the iconsData array
  function processNextIcon() {
    if (icons.length > 0) {
      // Remove the next icon from the array and start downloading it
      const nextIcon = icons.shift();
      downloadIcon(nextIcon!);
    }
  }

  // Start processing the first batch of icons, up to the maximum number of simultaneous requests = 4
  for (let i = 0; i < maxRequests && icons.length > 0; i++) {
    processNextIcon();
  }
};
